import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  Observable,
  split,
} from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { createClient } from 'graphql-ws'
import { UserManager } from 'oidc-client-ts'
import { setAuthenticated } from 'cache/auth'
import { setUserActivity } from 'cache/user-activity'
import { CLIENT_ID, OPEN_ID_SETTINGS } from 'config/o-auth'

const STS_AUTHORITY = process.env.REACT_APP_STS_AUTHORITY
const TOKEN_ENDPOINT = process.env.REACT_APP_TOKEN_ENDPOINT
const REACT_APP_GRAPHQL_HEADER_SOURCE = process.env.REACT_APP_GRAPHQL_HEADER_SOURCE
const userManager = new UserManager(OPEN_ID_SETTINGS)

// Auth-related keys that should be cleared on authentication failures
const AUTH_KEYS = ['token', 'id_token', 'refresh_token', 'authUserEmailAddress'] as const

export const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => localStorage.removeItem(key))
  sessionStorage.clear()
  setAuthenticated(false)
}
const verifyUserActivity = new ApolloLink((operation, forward) => {
  setUserActivity()
  return forward(operation)
})

const cleanTypeName = new ApolloLink((operation: any, forward: any) => {
  if (operation.variables) {
    const omitTypename = (key: string, value: any) => (key === '__typename' ? undefined : value)
    operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename)
  }

  return forward(operation)
})

// Parses a jwt token and returns the payload
export function parseJwt(jwtStr: string) {
  try {
    // Replace URL-safe base64 characters with standard ones
    const parts = jwtStr.split('.')
    const decodedPayload = atob(parts[1])

    const parsedPayload = JSON.parse(decodedPayload)
    return parsedPayload
  } catch {
    return null
  }
}

// Check if the token is expired
export const isTokenExpired = (token: string): boolean => {
  const payload = parseJwt(token)
  if (!payload || typeof payload.exp !== 'number') {
    return true
  }
  const currentTime = Date.now() / 1000 // Calculate in seconds
  return payload.exp < currentTime
}

export const authLink = setContext((_, { headers }) => {
  const TOKEN = localStorage.getItem('token')
  const ID_TOKEN = localStorage.getItem('id_token')

  if (!TOKEN || !ID_TOKEN || isTokenExpired(TOKEN)) {
    // Trigger OAuth SSO login flow
    const redirectPath = location.pathname
    clearAuthStorage()
    // Store current path for redirect after login
    localStorage.setItem('frompath', redirectPath)
    userManager.signinRedirect()
    return { headers: { ...headers } }
  }

  return {
    headers: {
      ...headers,
      source: REACT_APP_GRAPHQL_HEADER_SOURCE,
      authorization: TOKEN ? `Bearer ${TOKEN}` : '',
    },
  }
})

export const refreshToken = () => {
  const refresh_token = localStorage.getItem('refresh_token')
  if (!refresh_token) {
    return Promise.reject(new Error('No refresh token available'))
  }
  const init = {
    method: 'POST',
    headers: { 'Content-Type': `application/x-www-form-urlencoded` },
    body: `refresh_token=${refresh_token}&grant_type=refresh_token&client_id=${CLIENT_ID}`,
  }
  return fetch(`${STS_AUTHORITY}${TOKEN_ENDPOINT}`, init)
    .then((res) => res.json())
    .then(({ access_token, refresh_token, id_token }) => {
      if (!access_token || !id_token) {
        throw new Error('Token refresh response missing access_token or id_token')
      }
      localStorage.setItem('token', access_token)
      localStorage.setItem('id_token', id_token)
      localStorage.setItem('refresh_token', refresh_token || '')
      setAuthenticated(true)
      window.dispatchEvent(
        new CustomEvent('virtual-agent:token:renewed', { detail: { token: access_token } })
      )
      return access_token as string
    })
}

export const errorLink = onError(({ networkError, operation, forward }: any) => {
  if (networkError) {
    if (networkError?.statusCode === 401) {
      return new Observable((observer) => {
        refreshToken()
          .then((accessToken) => {
            if (!accessToken) {
              const redirectPath = location.pathname
              clearAuthStorage()
              localStorage.setItem('frompath', redirectPath)
              userManager.signinRedirect()
              observer.error(new Error('Authentication failed'))
              return
            }
            const oldHeaders = operation.getContext().headers
            operation.setContext({
              headers: {
                ...oldHeaders,
                authorization: `Bearer ${accessToken}`,
              },
            })
            const subscriber = {
              next: observer.next.bind(observer),
              error: observer.error.bind(observer),
              complete: observer.complete.bind(observer),
            }
            forward(operation).subscribe(subscriber)
          })
          .catch(() => {
            const redirectPath = location.pathname
            clearAuthStorage()
            localStorage.setItem('frompath', redirectPath)
            userManager.signinRedirect()
            observer.error(new Error('Authentication failed'))
          })
      })
    }
  }
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_GRAPHQL_WS_URI ?? '',
    connectionParams: () => ({
      authorization: `Bearer ${localStorage.getItem('access_token') || ''}`,
    }),
  })
)

const httpLink = authLink
  .concat(cleanTypeName)
  .concat(verifyUserActivity)
  .concat(errorLink)
  .concat(new HttpLink({ uri: process.env.REACT_APP_GRAPHQL_URI }))

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return definition.kind === 'OperationDefinition' && definition.operation === 'subscription'
  },
  wsLink,
  httpLink
)

const apolloClient = new ApolloClient({
  cache: new InMemoryCache({
    typePolicies: {
      BusinessOptionsItem: {
        keyFields: ['text', 'name'],
      },
      DefaultOptionsItem: {
        keyFields: ['text', 'name'],
      },
    },
  }),
  link: splitLink,
})

export default apolloClient
